import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Key, Copy, RefreshCw, Settings, CheckCircle } from 'lucide-react';
import axios from 'axios';

interface PasswordSettings {
  length: number;
  include_lowercase: boolean;
  include_uppercase: boolean;
  include_numbers: boolean;
  include_symbols: boolean;
  exclude_ambiguous: boolean;
  count: number;
}

interface PasswordPreset {
  name: string;
  description: string;
  settings: PasswordSettings;
}

interface PasswordResult {
  passwords: string[];
  settings: PasswordSettings;
}

const PasswordGeneratorTool: React.FC = () => {
  const [settings, setSettings] = useState<PasswordSettings>({
    length: 12,
    include_lowercase: true,
    include_uppercase: true,
    include_numbers: true,
    include_symbols: true,
    exclude_ambiguous: false,
    count: 1
  });
  
  const [passwords, setPasswords] = useState<string[]>([]);
  const [presets, setPresets] = useState<PasswordPreset[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const response = await axios.get('/api/tools/password-generator/presets');
      setPresets(response.data.presets);
    } catch (err) {
      console.error('Failed to load presets:', err);
    }
  };

  const generatePasswords = async () => {
    setIsGenerating(true);
    setError(null);
    setCopiedIndex(null);

    try {
      const response = await axios.post('/api/tools/password-generator/generate', settings);
      setPasswords(response.data.passwords);
    } catch (err: any) {
      setError(err.response?.data?.detail || '密码生成失败');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (password: string, index: number) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const applyPreset = (preset: PasswordPreset) => {
    setSettings(preset.settings);
    setPasswords([]);
    setError(null);
  };

  const updateSettings = (key: keyof PasswordSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setPasswords([]);
    setError(null);
  };

  const getStrengthColor = (length: number, hasSymbols: boolean, hasNumbers: boolean, hasUpper: boolean, hasLower: boolean) => {
    const score = length + (hasSymbols ? 2 : 0) + (hasNumbers ? 1 : 0) + (hasUpper ? 1 : 0) + (hasLower ? 1 : 0);
    if (score >= 16) return 'text-green-600';
    if (score >= 12) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStrengthText = (length: number, hasSymbols: boolean, hasNumbers: boolean, hasUpper: boolean, hasLower: boolean) => {
    const score = length + (hasSymbols ? 2 : 0) + (hasNumbers ? 1 : 0) + (hasUpper ? 1 : 0) + (hasLower ? 1 : 0);
    if (score >= 16) return '强';
    if (score >= 12) return '中';
    return '弱';
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            密码生成器
          </CardTitle>
          <CardDescription>
            生成安全可靠的密码
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 快捷预设 */}
          <div>
            <h3 className="text-sm font-medium mb-3">快捷预设</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {presets.map((preset) => (
                <Button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  variant="outline"
                  className="h-auto p-3 text-left"
                >
                  <div>
                    <div className="font-medium text-sm">{preset.name}</div>
                    <div className="text-xs text-gray-500">{preset.description}</div>
                  </div>
                </Button>
              ))}
            </div>
          </div>

          {/* 密码长度 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              密码长度: {settings.length}
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="4"
                max="128"
                value={settings.length}
                onChange={(e) => updateSettings('length', parseInt(e.target.value))}
                className="flex-1"
              />
              <span className={`text-sm font-medium ${getStrengthColor(
                settings.length,
                settings.include_symbols,
                settings.include_numbers,
                settings.include_uppercase,
                settings.include_lowercase
              )}`}>
                {getStrengthText(
                  settings.length,
                  settings.include_symbols,
                  settings.include_numbers,
                  settings.include_uppercase,
                  settings.include_lowercase
                )}
              </span>
            </div>
          </div>

          {/* 字符类型 */}
          <div>
            <h3 className="text-sm font-medium mb-3">包含字符类型</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={settings.include_lowercase}
                  onChange={(e) => updateSettings('include_lowercase', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">小写字母 (a-z)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={settings.include_uppercase}
                  onChange={(e) => updateSettings('include_uppercase', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">大写字母 (A-Z)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={settings.include_numbers}
                  onChange={(e) => updateSettings('include_numbers', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">数字 (0-9)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={settings.include_symbols}
                  onChange={(e) => updateSettings('include_symbols', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">特殊字符 (!@#$%...)</span>
              </label>
            </div>
          </div>

          {/* 高级设置 */}
          <div>
            <Button
              onClick={() => setShowAdvanced(!showAdvanced)}
              variant="ghost"
              className="flex items-center gap-2 p-0 h-auto"
            >
              <Settings className="w-4 h-4" />
              高级设置
            </Button>
            {showAdvanced && (
              <div className="mt-3 space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.exclude_ambiguous}
                    onChange={(e) => updateSettings('exclude_ambiguous', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">排除易混淆字符 (0, O, 1, l, I)</span>
                </label>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    生成数量: {settings.count}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.count}
                    onChange={(e) => updateSettings('count', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 生成按钮 */}
          <Button
            onClick={generatePasswords}
            disabled={isGenerating}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                生成中...
              </>
            ) : (
              <>
                <Key className="w-4 h-4 mr-2" />
                生成密码
              </>
            )}
          </Button>

          {/* 错误信息 */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* 生成结果 */}
          {passwords.length > 0 && (
            <Card className="bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800">生成的密码</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {passwords.map((password, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 bg-white rounded-md border">
                    <code className="flex-1 text-sm font-mono bg-gray-100 p-2 rounded">
                      {password}
                    </code>
                    <Button
                      onClick={() => copyToClipboard(password, index)}
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-1"
                    >
                      {copiedIndex === index ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          已复制
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          复制
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PasswordGeneratorTool;